import { Flex, Skeleton, Tabs, Typography } from "antd";
import { useSportConfiguration } from "hooks/useSportConfiguration";
import { useFetchUniforms } from "hooks/useUniformApi";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { Uniform } from "./Uniform";

const Wrapper = styled.div`
  margin-top: 10px;

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    border-radius: 0 !important;
  }

  .ant-tabs-tab-active {
    border-top: 1px solid #ff0000 !important;
  }
`;

const NewTabKey = "new-uniform-tab-key";

type Props = {
  entityId: string;
};

export const Uniforms = ({ entityId }: Props) => {
  const { formatMessage } = useIntl();
  const [currentTabKey, setCurrentTabKey] = useState<string>();
  const { data: uniformsResponse, isPending: isUniformsLoading } = useFetchUniforms({ entityId });
  const { configuration } = useSportConfiguration();
  const [showNewTab, setShowNewTab] = useState<boolean>();

  const onAddedUniformHandler = useCallback(async (uniformId: string) => {
    setCurrentTabKey(uniformId);
    setShowNewTab(false);
  }, []);

  const onTabsEditHandler = async (key: any, action: "add" | "remove") => {
    if (action === "add") {
      setShowNewTab(true);
      setCurrentTabKey(NewTabKey);
    } else if (key === NewTabKey) {
      setShowNewTab(false);
      setCurrentTabKey("");
    }
  };

  const tabs = useMemo(() => {
    const array = (uniformsResponse?.data || []).map((x: any) => ({
      label: x.nameLocal,
      key: x.uniformId,
      closable: false,
      children: <Uniform key={x.uniformId} entityId={entityId} uniform={x} onDeleted={() => setCurrentTabKey("")} />,
    }));

    if (showNewTab) {
      array.push({
        label: "*",
        key: NewTabKey,
        closable: true,
        children: <Uniform entityId={entityId} onAdded={onAddedUniformHandler} />,
      });
    }

    return array;
  }, [showNewTab, entityId, uniformsResponse, onAddedUniformHandler]);

  // Current sport does not support uniforms
  if (
    !configuration ||
    !configuration.uniforms ||
    configuration.uniforms.items.length === 0 ||
    configuration.uniforms.items.flatMap((x) => x.itemTypes).length === 0
  ) {
    return;
  }
  return (
    <Flex vertical>
      <Typography.Title level={4}>{formatMessage({ id: "uniformModal.title" })}</Typography.Title>
      {isUniformsLoading && <Skeleton />}
      {!isUniformsLoading && (
        <Wrapper>
          <Tabs
            activeKey={isEmpty(currentTabKey) ? tabs?.[0]?.key : currentTabKey}
            type="editable-card"
            items={tabs}
            onEdit={onTabsEditHandler}
            onChange={(key) => setCurrentTabKey(key)}
            hideAdd={showNewTab}
          />
        </Wrapper>
      )}
    </Flex>
  );
};
